import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';

/**
 * Passwordless student signup. Visually mirrors ContactPage: same 3-step
 * progressive flow, same tokens, same field set minus "message" and
 * "preferred format". Posts to /auth/register-quick; backend creates the
 * user and sends a welcome-set-password email. Once the student clicks
 * that link they land on /reset-password to choose a password.
 */

type Category = 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';

interface Subject {
  id: string;
  name: string;
  category: Category;
  isActive: boolean;
}

type Level = Category;
type ParentOrStudent = 'Parent' | 'Student';

const LEVEL_OPTIONS: { value: Level; label: string }[] = [
  { value: 'ELEVEN_PLUS', label: '11+' },
  { value: 'GCSE', label: 'GCSE' },
  { value: 'A_LEVEL', label: 'A-level' },
  { value: 'UNIVERSITY', label: 'University / Oxbridge' },
];

const YEAR_GROUPS = [
  'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8', 'Year 9',
  'Year 10', 'Year 11', 'Year 12', 'Year 13', 'Other',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite') || '';
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [yearGroup, setYearGroup] = useState<string>('');
  const [level, setLevel] = useState<Level | ''>('');
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);

  // Step 2
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<ParentOrStudent | ''>('');

  // Step 3
  const [about, setAbout] = useState('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyExists, setAlreadyExists] = useState(false);
  const [error, setError] = useState('');

  // Subjects
  const [subjects, setSubjects] = useState<Subject[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/subjects');
        if (cancelled) return;
        setSubjects(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setSubjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { setSelectedSubjects([]); }, [level]);

  const filteredSubjects = useMemo(() => {
    if (!level) return [];
    return subjects.filter(s => s.category === level && s.isActive !== false);
  }, [subjects, level]);

  const toggleSubject = (name: string) => {
    setSelectedSubjects(prev => prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]);
  };

  const step1Valid = !!level;
  const step2Valid = name.trim().length > 0 && EMAIL_RE.test(email.trim());
  const step3Valid = true; // Step 3 is optional "about" + final submit

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!step1Valid || !step2Valid) return;
    setError('');
    setSubmitting(true);

    const descriptionLines = [
      yearGroup ? `Year group: ${yearGroup}` : null,
      level ? `Level: ${LEVEL_OPTIONS.find(o => o.value === level)?.label || ''}` : null,
      role ? `${role}` : null,
      about.trim() ? `About: ${about.trim()}` : null,
    ].filter(Boolean).join('\n');

    try {
      const res = await api.post('/auth/register-quick', {
        name: name.trim(),
        email: email.trim(),
        phoneNumber: phone.trim(),
        subjects: selectedSubjects,
        description: descriptionLines,
        ...(inviteCode ? { inviteCode } : {}),
      });
      setSubmitted(true);
      setAlreadyExists(res.data?.alreadyExists === true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    setError('');
    if (step === 1 && step1Valid) setStep(2);
    else if (step === 2 && step2Valid) setStep(3);
  };
  const goBack = () => {
    setError('');
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  return (
    <main className="bg-cream">
      <Helmet>
        <title>Create your account — EJL Education</title>
        <meta name="description" content="Create an EJL Education account to book and manage tutoring sessions." />
      </Helmet>
      <section className="section">
        <div className="container-content">
          <h1 className="mb-4">Create your account</h1>
          <p className="text-muted max-w-2xl text-body">
            Tell me a little about what you're working on. I'll set up your account and send a link to pick a password.
          </p>
        </div>
      </section>

      <section className="border-t border-rule py-16 md:py-20">
        <div className="container-content">
          <div className="max-w-2xl">
            {submitted ? (
              <div className="rule-top text-center">
                <p className="mt-6 text-body text-ink font-serif">
                  {alreadyExists
                    ? `You already have an account under ${email}. Check your inbox for a previous welcome email, or log in.`
                    : `Check ${email} for a link to set your password and log in.`}
                </p>
                <div className="mt-6">
                  <Link to="/login" className="btn btn-primary">
                    Go to login
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-10">
                  <div
                    className="grid grid-cols-3 gap-2 mb-3"
                    role="progressbar"
                    aria-valuemin={1}
                    aria-valuemax={3}
                    aria-valuenow={step}
                  >
                    {[1, 2, 3].map(n => (
                      <div key={n} className={`h-[2px] ${n === step ? 'bg-ochre' : 'bg-rule'}`} />
                    ))}
                  </div>
                  <p className="eyebrow">Step {step} of 3</p>
                </div>

                {error && (
                  <div className="mb-6 border border-rule bg-paper p-4">
                    <p className="text-ink text-[15px]">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  {step === 1 && (
                    <div className="space-y-8">
                      <h2 className="mb-2">What are you studying?</h2>

                      <div>
                        <label htmlFor="yearGroup" className="block text-[15px] font-medium text-ink mb-2">
                          Year group
                        </label>
                        <select
                          id="yearGroup"
                          value={yearGroup}
                          onChange={(e) => setYearGroup(e.target.value)}
                          className="input"
                        >
                          <option value="">Select year group</option>
                          {YEAR_GROUPS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>

                      <fieldset>
                        <legend className="block text-[15px] font-medium text-ink mb-3">Level</legend>
                        <div className="space-y-2">
                          {LEVEL_OPTIONS.map(opt => (
                            <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="radio"
                                name="level"
                                value={opt.value}
                                checked={level === opt.value}
                                onChange={() => setLevel(opt.value)}
                                className="text-ochre focus:ring-ochre"
                              />
                              <span className="text-body text-ink font-serif">{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      {level && (
                        <fieldset>
                          <legend className="block text-[15px] font-medium text-ink mb-3">Subjects of interest</legend>
                          {filteredSubjects.length === 0 ? (
                            <p className="text-muted text-body italic">
                              (No subjects listed for this level yet. Leave blank and mention what you need in step 3.)
                            </p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                              {filteredSubjects.map(s => (
                                <label key={s.id} className="flex items-center gap-3 border-t border-rule py-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedSubjects.includes(s.name)}
                                    onChange={() => toggleSubject(s.name)}
                                    className="text-ochre focus:ring-ochre"
                                  />
                                  <span className="text-body text-ink font-serif">{s.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </fieldset>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4">
                        <button
                          type="button"
                          onClick={goNext}
                          disabled={!step1Valid}
                          className="btn btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-8">
                      <h2 className="mb-2">Who are you?</h2>

                      <div>
                        <label htmlFor="name" className="block text-[15px] font-medium text-ink mb-2">
                          Name
                        </label>
                        <input
                          id="name" type="text"
                          value={name} onChange={(e) => setName(e.target.value)}
                          className="input" autoComplete="name"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-[15px] font-medium text-ink mb-2">
                          Email
                        </label>
                        <input
                          id="email" type="email"
                          value={email} onChange={(e) => setEmail(e.target.value)}
                          className="input" autoComplete="email" required
                        />
                        <p className="text-xs text-muted mt-1">We'll email you a link to set your password.</p>
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-[15px] font-medium text-ink mb-2">
                          Phone (optional)
                        </label>
                        <input
                          id="phone" type="tel"
                          value={phone} onChange={(e) => setPhone(e.target.value)}
                          className="input" autoComplete="tel"
                        />
                      </div>

                      <fieldset>
                        <legend className="block text-[15px] font-medium text-ink mb-3">
                          Are you the parent or student?
                        </legend>
                        <div className="space-y-2">
                          {(['Parent', 'Student'] as ParentOrStudent[]).map(r => (
                            <label key={r} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="radio" name="role" value={r}
                                checked={role === r} onChange={() => setRole(r)}
                                className="text-ochre focus:ring-ochre"
                              />
                              <span className="text-body text-ink font-serif">{r}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between pt-4">
                        <button type="button" onClick={goBack} className="btn btn-secondary w-full sm:w-auto">Back</button>
                        <button
                          type="button" onClick={goNext}
                          disabled={!step2Valid}
                          className="btn btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-8">
                      <h2 className="mb-2">Anything else?</h2>

                      <div>
                        <label htmlFor="about" className="block text-[15px] font-medium text-ink mb-2">
                          A bit about you (optional)
                        </label>
                        <textarea
                          id="about" rows={5}
                          value={about} onChange={(e) => setAbout(e.target.value)}
                          className="input"
                          placeholder="Goals, exam dates, anything you'd like Ethan to know before the first session."
                        />
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between pt-4">
                        <button type="button" onClick={goBack} className="btn btn-secondary w-full sm:w-auto">Back</button>
                        <button
                          type="submit"
                          disabled={submitting || !step2Valid}
                          className="btn btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submitting ? 'Creating…' : 'Create account'}
                        </button>
                      </div>
                    </div>
                  )}
                </form>

                <p className="mt-10 text-sm text-muted">
                  Already have an account? <Link to="/login" className="text-ochre hover:underline">Log in</Link>.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default RegisterPage;
